import { Fragment, useState, useRef, useEffect, useCallback } from "react";
import { apiFetch } from "../../../api";
import { trackEvent } from "../../../analytics/posthog";
import { AiSparkleIcon } from "../learning/components/AiChatPopup";
import s from "./AiTutorPanel.module.css";

/* Lightweight markdown renderer */

function renderInline(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={`${keyPrefix}-b-${i}`}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

const OL_RE = /^(\d+)\.\s+(.*)$/;
const UL_RE = /^[-*]\s+(.*)$/;

function MarkdownText({ text }) {
  if (!text) return null;
  const blocks = String(text).split(/\n\s*\n/);
  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split("\n").filter((l) => l.trim().length > 0);
        if (lines.length === 0) return null;
        const isOrdered = lines.every((l) => OL_RE.test(l));
        const isBullet = !isOrdered && lines.every((l) => UL_RE.test(l));
        if (isOrdered) {
          return (
            <ol key={bi}>
              {lines.map((l, li) => {
                const m = l.match(OL_RE);
                return <li key={li}>{renderInline(m[2], `${bi}-${li}`)}</li>;
              })}
            </ol>
          );
        }
        if (isBullet) {
          return (
            <ul key={bi}>
              {lines.map((l, li) => {
                const m = l.match(UL_RE);
                return <li key={li}>{renderInline(m[1], `${bi}-${li}`)}</li>;
              })}
            </ul>
          );
        }
        return (
          <p key={bi}>
            {lines.map((l, li) => (
              <span key={li}>
                {renderInline(l, `${bi}-${li}`)}
                {li < lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </>
  );
}

/* SVG Icons */

function SendIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z" />
    </svg>
  );
}

const WELCOME_MESSAGE = "Hej! How can I help you :)";

let _msgId = 0;
let _restore = null;

export default function AiTutorPanel() {
  const [messages, setMessages] = useState(() => _restore?.messages || []);
  const [sessionId, setSessionId] = useState(() => _restore?.sessionId || null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [welcomeReady, setWelcomeReady] = useState(() => _restore?.messages?.length > 0);

  const messagesEndRef = useRef(null);
  const messagesRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const stateRef = useRef({});
  stateRef.current = { messages, sessionId };

  useEffect(() => {
    const restored = _restore;
    _restore = null;
    trackEvent("ai_tutor_opened", {
      has_history: !!(restored && restored.messages.length > 0),
    });
    if (!restored || restored.messages.length === 0) {
      const t = setTimeout(() => setWelcomeReady(true), 1500);
      return () => { clearTimeout(t); abortRef.current?.abort(); };
    }
    return () => {
      abortRef.current?.abort();
      const snap = stateRef.current;
      _restore = snap.messages.length > 0 ? { ...snap } : null;
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Scroll to bottom when virtual keyboard opens on mobile
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let t;
    function onResize() {
      clearTimeout(t);
      t = setTimeout(() => {
        const el = messagesRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      }, 50);
    }
    vv.addEventListener("resize", onResize);
    return () => { vv.removeEventListener("resize", onResize); clearTimeout(t); };
  }, []);

  const sendMessage = useCallback(
    async (overrideText) => {
      const text = (overrideText || input).trim();
      if (!text || loading) return;

      if (!overrideText) setInput("");
      setMessages((prev) => [
        ...prev,
        { id: ++_msgId, role: "user", content: text },
      ]);
      setLoading(true);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await apiFetch("/api/v1/ai-tutor/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: text, session_id: sessionId, origin: "ai_tutor" }),
          signal: controller.signal,
        });

        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        setSessionId(data.session_id);
        trackEvent("ai_tutor_message_sent", { session_id: data.session_id });
        setMessages((prev) => [
          ...prev,
          {
            id: ++_msgId,
            role: "assistant",
            content: data.reply_swedish || data.reply,
            gloss: data.reply_english || null,
          },
        ]);
      } catch (e) {
        if (e.name === "AbortError") return;
        setMessages((prev) => [
          ...prev,
          { id: ++_msgId, role: "assistant", content: "Sorry, I couldn't respond. Please try again.", isError: true },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [input, loading, sessionId]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const handleInputChange = useCallback((e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 80) + "px";
  }, []);

  const handleClear = useCallback(() => {
    abortRef.current?.abort();
    _restore = null;
    setMessages([]);
    setSessionId(null);
    setLoading(false);
    setWelcomeReady(false);
    setTimeout(() => setWelcomeReady(true), 1500);
  }, []);

  const hasMessages = messages.length > 0;

  return (
    <div className={s.panel}>
      <div className={s.messages} ref={messagesRef}>
        <div className={s.chatHeader}>
          <div className={s.chatHeaderLeft}>
            <div className={s.chatHeaderAvatar}>
              <AiSparkleIcon className={s.chatHeaderAvatarIcon} />
            </div>
            <div>
              <span className={s.chatHeaderName}>AI Tutor</span>
              <span className={s.chatHeaderStatus}>
                <span className={s.statusDot} />
                Online
              </span>
            </div>
          </div>
          <div className={s.chatHeaderRight}>
            <button className={s.newChatBtn} onClick={handleClear} title="New conversation">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              New chat
            </button>
          </div>
        </div>

        {/* Flex spacer */}
        <div style={{ flex: "1 1 auto" }} />

        {/* Welcome message */}
        {!hasMessages && !welcomeReady && (
          <div className={`${s.msg} ${s.msgAi}`}>
            <div className={s.aiAvatar}>
              <AiSparkleIcon className={s.aiAvatarIcon} />
            </div>
            <div className={s.typing}>
              <span className={s.typingDot} />
              <span className={s.typingDot} />
              <span className={s.typingDot} />
            </div>
          </div>
        )}
        {!hasMessages && welcomeReady && (
          <div className={`${s.msg} ${s.msgAi}`}>
            <div className={s.aiAvatar}>
              <AiSparkleIcon className={s.aiAvatarIcon} />
            </div>
            <div className={s.msgBubble}>
              {WELCOME_MESSAGE}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <Fragment key={msg.id}>
            <div className={`${s.msg} ${msg.role === "user" ? s.msgUser : s.msgAi}`}>
              {msg.role === "assistant" && (
                <div className={s.aiAvatar}>
                  <AiSparkleIcon className={s.aiAvatarIcon} />
                </div>
              )}
              <div className={s.msgBubble}>
                {msg.role === "assistant" ? <MarkdownText text={msg.content} /> : msg.content}
                {msg.gloss && <p className={s.bubbleGloss}>{msg.gloss}</p>}
              </div>
            </div>
          </Fragment>
        ))}

        {loading && (
          <div className={`${s.msg} ${s.msgAi}`}>
            <div className={s.aiAvatar}>
              <AiSparkleIcon className={s.aiAvatarIcon} />
            </div>
            <div className={s.typing}>
              <span className={s.typingDot} />
              <span className={s.typingDot} />
              <span className={s.typingDot} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={s.inputArea}>
        <div className={s.inputBox}>
          <textarea
            ref={inputRef}
            className={s.input}
            placeholder="Ask me anything about Swedish..."
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              setTimeout(() => {
                const el = messagesRef.current;
                if (el) el.scrollTop = el.scrollHeight;
              }, 100);
            }}
            rows={1}
          />
          <button
            className={s.sendBtn}
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            title="Send"
          >
            <SendIcon className={s.sendIcon} />
          </button>
        </div>
      </div>
    </div>
  );
}
